// Composable for custom dialog confirmations
// Based on production DialogService

interface DialogOptions {
  title?: string
  confirmText?: string
  cancelText?: string
  type?: 'warning' | 'danger' | 'info'
}

interface AlertOptions {
  title?: string
  type?: 'success' | 'error' | 'warning' | 'info'
  buttonText?: string
}

class DialogService {
  private activeDialog: HTMLElement | null = null

  /**
   * Show a confirmation dialog
   */
  async confirm(message: string, options: DialogOptions = {}): Promise<boolean> {
    const {
      title = 'Confirm Action',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      type = 'warning'
    } = options

    return new Promise((resolve) => {
      // Remove any existing dialog
      this.close()

      // Create dialog overlay
      const overlay = document.createElement('div')
      overlay.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] animate-fadeIn'
      overlay.style.animation = 'fadeIn 0.2s ease-out'

      // Determine icon and colors based on type
      let icon = 'fa-exclamation-triangle'
      let iconColor = 'text-yellow-400'
      let buttonColor = 'bg-purple-600 hover:bg-purple-500'

      if (type === 'danger') {
        icon = 'fa-exclamation-circle'
        iconColor = 'text-red-400'
        buttonColor = 'bg-red-600 hover:bg-red-500'
      } else if (type === 'info') {
        icon = 'fa-info-circle'
        iconColor = 'text-blue-400'
        buttonColor = 'bg-blue-600 hover:bg-blue-500'
      }

      // Create dialog content
      overlay.innerHTML = `
        <div class="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-md w-full mx-4 animate-slideUp" 
             style="animation: slideUp 0.3s ease-out;">
          <div class="p-6">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0">
                <i class="fas ${icon} ${iconColor} text-3xl"></i>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-bold text-white mb-2">${title}</h3>
                <p class="text-gray-300 text-sm leading-relaxed whitespace-pre-line">${message}</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-800/50 px-6 py-4 flex gap-3 justify-end rounded-b-lg border-t border-gray-700">
            <button id="dialog-cancel" 
                    class="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors">
              ${cancelText}
            </button>
            <button id="dialog-confirm" 
                    class="px-4 py-2 ${buttonColor} text-white rounded-lg font-medium transition-colors">
              ${confirmText}
            </button>
          </div>
        </div>
      `

      // Add event listeners
      const confirmBtn = overlay.querySelector('#dialog-confirm') as HTMLButtonElement
      const cancelBtn = overlay.querySelector('#dialog-cancel') as HTMLButtonElement

      const handleConfirm = () => {
        this.close()
        resolve(true)
      }

      const handleCancel = () => {
        this.close()
        resolve(false)
      }

      confirmBtn?.addEventListener('click', handleConfirm)
      cancelBtn?.addEventListener('click', handleCancel)
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          handleCancel()
        }
      })

      // Handle Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCancel()
          document.removeEventListener('keydown', handleEscape)
        }
      }
      document.addEventListener('keydown', handleEscape)

      // Add to DOM
      document.body.appendChild(overlay)
      this.activeDialog = overlay

      // Focus confirm button
      setTimeout(() => confirmBtn?.focus(), 100)
    })
  }

  /**
   * Show an alert/notification dialog
   */
  async alert(message: string, options: AlertOptions = {}): Promise<void> {
    const {
      title = 'Notification',
      type = 'info',
      buttonText = 'OK'
    } = options

    return new Promise((resolve) => {
      // Remove any existing dialog
      this.close()

      // Create dialog overlay
      const overlay = document.createElement('div')
      overlay.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] animate-fadeIn'
      overlay.style.animation = 'fadeIn 0.2s ease-out'

      // Determine icon and colors based on type
      let icon = 'fa-info-circle'
      let iconColor = 'text-blue-400'
      let buttonColor = 'bg-blue-600 hover:bg-blue-500'

      if (type === 'success') {
        icon = 'fa-check-circle'
        iconColor = 'text-green-400'
        buttonColor = 'bg-green-600 hover:bg-green-500'
      } else if (type === 'error') {
        icon = 'fa-times-circle'
        iconColor = 'text-red-400'
        buttonColor = 'bg-red-600 hover:bg-red-500'
      } else if (type === 'warning') {
        icon = 'fa-exclamation-triangle'
        iconColor = 'text-yellow-400'
        buttonColor = 'bg-yellow-600 hover:bg-yellow-500'
      }

      // Create dialog content
      overlay.innerHTML = `
        <div class="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-md w-full mx-4 animate-slideUp" 
             style="animation: slideUp 0.3s ease-out;">
          <div class="p-6">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0">
                <i class="fas ${icon} ${iconColor} text-3xl"></i>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-bold text-white mb-2">${title}</h3>
                <p class="text-gray-300 text-sm leading-relaxed whitespace-pre-line">${message}</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-800/50 px-6 py-4 flex gap-3 justify-end rounded-b-lg border-t border-gray-700">
            <button id="dialog-ok" 
                    class="px-4 py-2 ${buttonColor} text-white rounded-lg font-medium transition-colors">
              ${buttonText}
            </button>
          </div>
        </div>
      `

      // Add event listener
      const okBtn = overlay.querySelector('#dialog-ok') as HTMLButtonElement

      const handleClose = () => {
        this.close()
        resolve()
      }

      okBtn?.addEventListener('click', handleClose)
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          handleClose()
        }
      })

      // Handle Escape and Enter keys
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Escape' || e.key === 'Enter') {
          handleClose()
          document.removeEventListener('keydown', handleKeyPress)
        }
      }
      document.addEventListener('keydown', handleKeyPress)

      // Add to DOM
      document.body.appendChild(overlay)
      this.activeDialog = overlay

      // Focus OK button
      setTimeout(() => okBtn?.focus(), 100)
    })
  }

  /**
   * Close the active dialog
   */
  close() {
    if (this.activeDialog) {
      this.activeDialog.remove()
      this.activeDialog = null
    }
  }
}

// Create singleton instance
const dialogService = new DialogService()

// Export composable
export function useDialog() {
  return {
    confirm: dialogService.confirm.bind(dialogService),
    alert: dialogService.alert.bind(dialogService),
    close: dialogService.close.bind(dialogService)
  }
}
